import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Label as _Label } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  // Chip,
  Grid,
  MenuItem,
  Popover,
  Select,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { format } from 'date-fns';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import { useAppSelector } from 'src/hooks/useStore';
import {
  getOrganizationMemberLabel,
  getOrganizationMembers,
  type OrganizationMember,
} from 'src/services/organizations.service';

// Register countries locale once, outside component
countries.registerLocale(enLocale);

type Props = {
  open: boolean;
  onClose: () => void;
  onAddFilter: (key: string, value: string | string[]) => void;
  defaults?: Partial<Record<string, string>>;
  anchorEl?: HTMLElement | null;
};

export const statusOptions = [
  {
    id: '',
    name: 'All',
  },
  {
    id: 'STARTED',
    name: 'STARTED',
  },
  {
    id: 'COMPLETED',
    name: 'COMPLETED',
  },
  {
    id: 'PENDING',
    name: 'PENDING',
  },
  {
    id: 'CANCELED',
    name: 'CANCELED',
  },
];

export const validationOptions = [
  {
    id: '',
    name: 'All',
  },
  {
    id: 'PASS',
    name: 'PASS',
  },
  {
    id: 'FAIL',
    name: 'FAIL',
  },
  {
    id: 'PENDING',
    name: 'PENDING',
  },
  {
    id: 'NEEDS_REVIEW',
    name: 'NEEDS REVIEW',
  },
];

export const genderOptions = [
  {
    id: 'M',
    name: 'Male',
  },
  {
    id: 'F',
    name: 'Female',
  },
  {
    id: '',
    name: 'Other',
  },
];

export const documentOptions = [
  {
    id: 'Passport',
    name: 'Passport',
  },
  {
    id: 'IdentificationCard',
    name: 'ID',
  },
];

const Panel = styled('div')(({}) => ({
  width: '100%',
  maxWidth: '1000px',
  background: '#fff',
  borderRadius: 8,
  boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
}));

const documentTypes = ['IdentificationCard', 'Passport', 'ResidenceDocument'];

const proofOfAddressOptions = [
  'Utility Bill',
  'Bank Statement',
  'Void Cheque',
  'Credit Card Statement',
  'Tax Statement',
  'Official Letter',
  'Credit Card',
  'Identity Document',
  'Other',
];

const documentFrontSubtypeOptions = [
  'DOMESTIC_IDENTIFICATION_CARD',
  'DRIVER_LICENSE',
  'DRIVER_LICENSE_UNDER21',
  'DRIVERS_LICENSE',
  'DRIVERS_LICENSE_UNDER21',
  'ECOWAS',
  'EMERGENCY_PASSPORT',
  'FEDERAL_IDENTIFICATION',
  'FICTIONAL_CERTIFICATE',
  'HEALTH_CARD',
  'IDENTIFICATION_CARD',
  'IDENTIFICATION_CARD_ALLAGES',
  'IDENTIFICATION_CARD_UNDER21',
  'INDIAN_STATUS_CERTIFICATE',
  'INTERNATIONAL_DRIVERS_LICENSE',
  'INTERNATIONAL_DRIVING_PERMIT',
  'JUNIOR_DRIVERS_LICENSE',
  'LEARNER_PERMIT',
  'LONG_TERM_VISIT_PASS',
  'MEDICAL_CARD',
  'NATIONAL_IDENTIFICATION_CARD',
  'NATIONAL_PASSPORT',
  'PASSPORT',
  'PASSPORT_CARD_ALLAGES',
  'POLICE_IDENTIFICATION_CARD',
  'PROVISIONAL_DRIVER_LICENSE',
  'PROVISIONAL_DRIVERS_LICENSE',
  'PUBLIC_SERVICES',
  'RESIDENCE_CARD',
  'RESIDENCE_DOCUMENT',
  'RESIDENCE_PERMIT',
  'SENTRI',
  'SERVICE_PASSPORT',
  'TAX_IDENTIFICATION',
  'TEMP_GERMAN_IDCARD',
  'TEMPORARY_PASSPORT',
  'TEMPORARY_PROTECTION_IDENTITY_DOCUMENT',
  'TEMPORARY_RESIDENCE_PERMIT',
  'UMID_CARD',
  'VISA',
  'VOTER_IDENTIFICATION_CARD',
  'WHITE_BACK',
  'WORK_PERMIT',
];

const documentBackSubtypeOptions = [
  'BLACK_BACK',
  'CERTIFICATE_OF_INDIAN_STATUS',
  'DOMESTIC_DOCUMENT_OF_IDENTITY',
  'DOMESTIC_IDENTIFICATION_CARD',
  'DRIVER_LICENSE',
  'DRIVERS_LICENSE',
  'FICTIONAL_CERTIFICATE',
  'IDENTIFICATION_CARD',
  'IDENTIFICATION_CARD_FOR_FOREIGNERS',
  'IDENTIFICATION_CARD_UNDER21',
  'MEDICAL_CARD',
  'NATIONAL_PASSPORT',
  'PERMANENT_RESIDENT_CARD_ALLAGES',
  'RESIDENCE_DOCUMENT',
  'RESIDENCE_PERMIT',
  'SENTRI',
  'TAX_IDENTIFICATION',
  'TRIBAL_IDENTIFICATION_CARD',
  'UMID_CARD',
  'VOTER_IDENTIFICATION',
  'WHITE_BACK',
];
const providerOptions = [
  {
    id: 'incode',
    name: 'Incode',
  },
  {
    id: 'idverse',
    name: 'Idverse',
  },
  {
    id: 'staple',
    name: 'Staple',
  },
  {
    id: 'schufa',
    name: 'Schufa',
  },
  {
    id: 'lexisNexis',
    name: 'LexisNexis',
  },
  {
    id: 'tink',
    name: 'Tink',
  },
  {
    id: 'EKATA',
    name: 'EKATA',
  },
];

export const productOptions = [
  {
    id: 'POI',
    name: 'POI',
  },
  {
    id: 'POA',
    name: 'POA',
  },
  {
    id: 'Affordability',
    name: 'Affordability',
  },
];

export default function NewResultsFilters({
  open,
  onClose: _onClose,
  onAddFilter: _onAddFilter,
  defaults,
  anchorEl,
}: Props) {
  const [_page, _setPage] = useState(0);
  const [_first, _setFirst] = useState(20);
  const navigate = useNavigate();
  const location = useLocation();
  const { getIdTokenClaims } = useAuth0();
  const selectedOrganization = useAppSelector(
    state => state.organization.selectedOrganization,
  );
  const liveData = useAppSelector(state => state.sliceTransaction.liveData);
  const [_anchorEl, _setAnchorEl] = useState<null | HTMLElement>(null);
  const [openItem, setOpenItem] = useState<string | null>(null);
  const [inputValues, setInputValues] = useState<{
    [key: string]: string | string[];
  }>({});
  const [savedFilters, setSavedFilters] = useState<
    { key: string; label: string; value: string | string[] }[]
  >([]);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [selectedAssignee, setSelectedAssignee] =
    useState<OrganizationMember | null>(null);
  const [assigneeInput, setAssigneeInput] = useState('');
  const membersSearchTimerRef = useRef<number | undefined>();

  const loadMembers = useCallback(
    async (name?: string) => {
      if (!selectedOrganization?.id) {
        setMembers([]);
        return;
      }
      setMembersLoading(true);
      try {
        const { __raw } = (await getIdTokenClaims()) as any;
        const list = await getOrganizationMembers(
          selectedOrganization.id,
          __raw,
          { name, liveData },
        );
        setMembers(list);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch organization members:', error);
        setMembers([]);
      } finally {
        setMembersLoading(false);
      }
    },
    [getIdTokenClaims, liveData, selectedOrganization?.id],
  );

  useEffect(() => {
    if (openItem === 'assignee') {
      loadMembers();
    }
  }, [openItem, loadMembers]);

  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [issueDate, setIssueDate] = useState<Date | null>(new Date());
  const [birthDate, setBirthDate] = useState<Date | null>(new Date());
  const [expiredAt, setExpiredAt] = useState<Date | null>(new Date());

  // Removed fetching side effects; parent page is responsible for fetching on URL changes

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const filters = [];
    queryParams.forEach((value, key) => {
      filters.push({ key, label: key, value });
    });

    setSavedFilters(filters);

    const initialInputValues = {};
    filters.forEach(filter => {
      initialInputValues[filter.key] = filter.value;
    });
    setInputValues(initialInputValues);
  }, [location.search]);

  const handleClose = () => {
    setOpenItem(null);
    _onClose?.();
  };

  const handleMenuItemClick = (item: string) => {
    setOpenItem(openItem === item ? null : item);
  };

  const handleInputChange = (
    event: { target: { value: string | string[] } },
    item: string,
  ) => {
    const value = event.target.value;
    setInputValues(prev => ({
      ...prev,
      [item]: value,
    }));
  };

  const handleStartDateChange = (date: Date | null) => {
    if (date) {
      setStartDate(date);
    }
  };
  const handleIssueDateChange = (date: Date | null) => {
    if (date) {
      setIssueDate(date);
    }
  };
  const handleBirthDateChange = (date: Date | null) => {
    if (date) {
      setBirthDate(date);
    }
  };
  const handleExpiredAtChange = (date: Date | null) => {
    if (date) {
      setExpiredAt(date);
    }
  };

  const dateConfig = {
    'Issue Date': { value: issueDate, handler: handleIssueDateChange },
    'Exp. Date': { value: expiredAt, handler: handleExpiredAtChange },
    Birthday: { value: birthDate, handler: handleBirthDateChange },
    'Event start date': { value: startDate, handler: handleStartDateChange },
  };

  const updateURLWithFilters = (
    filters: Array<{ key: string; label: string; value: string | string[] }>,
  ) => {
    const queryParams = new URLSearchParams();
    filters.forEach(filter => {
      if (filter.key === 'provider' && Array.isArray(filter.value)) {
        queryParams.append('provider', filter.value.join(','));
      } else if (typeof filter.value === 'string') {
        queryParams.append(filter.key, filter.value);
      }
    });

    navigate({
      pathname: location.pathname,
      search: `?${queryParams.toString()}`,
    });
  };

  const handleSave = (item: string) => {
    if (item === 'assignee') {
      // Prefer exact member selection → assignedUserId; else free-text → assignedUserName
      if (selectedAssignee?.user_id) {
        const withoutAssignee = savedFilters.filter(
          f => f.key !== 'assignedUserId' && f.key !== 'assignedUserName',
        );
        const newFilter = {
          key: 'assignedUserId',
          label: getOrganizationMemberLabel(selectedAssignee),
          value: selectedAssignee.user_id,
        };
        const updated = [...withoutAssignee, newFilter];
        setSavedFilters(updated);
        localStorage.setItem('savedFilters', JSON.stringify(updated));
        setSelectedAssignee(null);
        setAssigneeInput('');
        setInputValues(prev => ({ ...prev, assignee: '' }));
        handleClose();
        updateURLWithFilters(updated);
        return;
      }
      const nameQuery = assigneeInput.trim();
      if (!nameQuery) {
        handleClose();
        return;
      }
      const withoutAssignee = savedFilters.filter(
        f => f.key !== 'assignedUserId' && f.key !== 'assignedUserName',
      );
      const newFilter = {
        key: 'assignedUserName',
        label: 'Assignee',
        value: nameQuery,
      };
      const updated = [...withoutAssignee, newFilter];
      setSavedFilters(updated);
      localStorage.setItem('savedFilters', JSON.stringify(updated));
      setSelectedAssignee(null);
      setAssigneeInput('');
      setInputValues(prev => ({ ...prev, assignee: '' }));
      handleClose();
      updateURLWithFilters(updated);
      return;
    }

    const value = inputValues[item];

    // Handle empty values
    if (
      !value ||
      (Array.isArray(value) && value.length === 0) ||
      (typeof value === 'string' && !value.trim())
    ) {
      handleClose();
      return;
    }

    // Special handling for dates
    if (item === 'createdStartDate') {
      const newValue = startDate ? format(startDate, 'yyyy-MM-dd') : '';
      if (!newValue) {
        handleClose();
        return;
      }
      const newFilter = { key: item, label: item, value: newValue };
      updateFilters(newFilter);
      return;
    }

    // Special handling for provider array
    if (item === 'provider' && Array.isArray(value)) {
      const newFilter = {
        key: item,
        label: item,
        value,
      };
      updateFilters(newFilter); // single filter with array value
      return;
    }

    // Handle all other string values
    const newFilter = { key: item, label: item, value: value.toString() };
    updateFilters(newFilter);
  };

  const _handleDelete = (option: FilterOption) => {
    const updatedFilters = savedFilters.filter(
      filter =>
        !(
          filter.key === option.key &&
          filter.label === option.label &&
          filter.value === option.value
        ),
    );
    setSavedFilters(updatedFilters);
    localStorage.setItem('savedFilters', JSON.stringify(updatedFilters));
    updateURLWithFilters(updatedFilters);
  };

  const handleKeyDown = e => {
    e.stopPropagation();
  };

  // Memoize country names to avoid recalculating on every render
  const countryNames = useMemo(
    () => countries.getNames('en', { select: 'official' }),
    [],
  );

  const [selectedCountry, setSelectedCountry] = useState('');

  const handleChangeList = (event, itemKey) => {
    const countryCodeAlpha2 = event.target.value;
    setSelectedCountry(countryCodeAlpha2);

    const countryNamePicked = countries.getName(countryCodeAlpha2, 'en');
    const countryCodeAlpha3 = countries.getAlpha3Code(countryNamePicked, 'en');

    handleInputChange({ target: { value: countryCodeAlpha3 } }, itemKey);
  };

  const updateFilters = (newFilter: FilterOption) => {
    const updatedFilters = savedFilters.filter(f => f.key !== newFilter.key);
    updatedFilters.push(newFilter);

    setSavedFilters(updatedFilters);
    localStorage.setItem('savedFilters', JSON.stringify(updatedFilters));
    setInputValues(prev => ({ ...prev, [newFilter.key]: '' }));
    handleClose();

    updateURLWithFilters(updatedFilters);
  };

  interface FilterOption {
    key: string;
    label: string;
    value: string | string[];
  }

  const isProviderSelected = (
    value: string | string[] | undefined,
    providerId: string,
  ): boolean => {
    if (!value) return false;
    if (Array.isArray(value)) return value.includes(providerId);
    if (typeof value === 'string') return value.split(',').includes(providerId);
    return false;
  };

  useEffect(() => {
    setInputValues(prev => ({
      ...prev,
      id: defaults?.id ?? prev?.id ?? '',
      referenceId: defaults?.referenceId ?? prev?.referenceId ?? '',
      fullName: defaults?.fullName ?? prev?.fullName ?? '',
      createdStartDate:
        defaults?.createdStartDate ?? prev?.createdStartDate ?? '',
      // preserve status and validation from URL/saved filters
    }));
  }, [defaults, open]);

  const containerRef = useRef<HTMLDivElement | null>(null);

  // Memoize filter arrays to prevent recreation on every render
  const sessionDataFilters = useMemo(
    () => [
      { key: 'id', label: 'SONIO session ID' },
      { key: 'referenceId', label: 'Reference ID' },
      { key: 'flowId', label: 'Flow ID' },
      { key: 'timeFrame', label: 'Time Frame' },
      { key: 'assignee', label: 'Assignee' },
      { key: 'validation', label: 'Result' },
      { key: 'status', label: 'Status' },
      { key: 'Product', label: 'Product' },
      { key: 'provider', label: 'Provider' },
    ],
    [],
  );

  const identificationFilters = useMemo(
    () => [
      { key: 'poaDocumentType', label: 'Document Type' },
      { key: 'documentFrontSubtype', label: 'Document Front Subtype' },
      { key: 'documentBackSubtype', label: 'Document Back Subtype' },
      { key: 'issuingCountry', label: 'Issuing Country' },
      { key: 'nationality', label: 'Nationality' },
      { key: 'firstName', label: 'First Name' },
      { key: 'lastName', label: 'Last Name' },
      { key: 'fullName', label: 'Full Name' },
    ],
    [],
  );

  const proofOfAddressFilters = useMemo(
    () => ['Document Type', 'Name', 'Address'],
    [],
  );

  const deviceDataFilters = useMemo(
    () => ['Browser', 'OS', 'Device type', 'Device model '],
    [],
  );

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      keepMounted={false}
      transitionDuration={200}
      TransitionProps={{
        timeout: 200,
      }}
      PaperProps={{
        sx: {
          minWidth: 800,
          maxWidth: 900,
          width: '90vw',
          maxHeight: '80vh',
          overflow: 'auto',
          '@media (max-width: 900px)': {
            minWidth: 600,
            width: '85vw',
          },
          '@media (max-width: 600px)': {
            minWidth: '90vw',
            width: '95vw',
          },
        },
      }}
    >
      <div ref={containerRef}>
        <Panel>
          <Grid container spacing={2} padding={2}>
            <Grid item xs={12} className="flex justify-between items-center">
              <Typography
                variant="h6"
                sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}
              >
                Filters
              </Typography>
              <Button onClick={handleClose} size="small" variant="outlined">
                Close
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="h6"
                sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}
              >
                Session Data
              </Typography>
            </Grid>
            {sessionDataFilters.map(item => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={item.key}>
                <MenuItem onClick={() => handleMenuItemClick(item.key)}>
                  <Typography sx={{ fontSize: '0.75rem' }}>
                    {item.label}
                  </Typography>
                </MenuItem>
                {openItem === item.key && (
                  <Box sx={{ p: 2 }}>
                    {item.key === 'status' ? (
                      <>
                        <Select
                          label={item.label}
                          variant="outlined"
                          size="small"
                          value={inputValues[item.key] || ''}
                          onChange={event => handleInputChange(event, item.key)}
                          fullWidth
                          sx={{ mb: 1 }}
                        >
                          {statusOptions.map(statusOption => (
                            <MenuItem
                              key={statusOption.id}
                              value={statusOption.id}
                            >
                              {statusOption.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </>
                    ) : item.key === 'validation' ? (
                      <>
                        <Select
                          label={item.label}
                          variant="outlined"
                          size="small"
                          value={inputValues[item.key] || ''}
                          onChange={event => handleInputChange(event, item.key)}
                          fullWidth
                          sx={{ mb: 1 }}
                        >
                          {validationOptions.map(validationOption => (
                            <MenuItem
                              key={validationOption.id}
                              value={validationOption.id}
                            >
                              {validationOption.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </>
                    ) : item.label === 'Provider' ? (
                      <Select
                        label="Provider"
                        multiple
                        variant="outlined"
                        size="small"
                        value={
                          Array.isArray(inputValues[item.key])
                            ? inputValues[item.key]
                            : (inputValues[item.key] as string)?.split(',') ||
                              []
                        }
                        onChange={event => handleInputChange(event, item.key)}
                        fullWidth
                        sx={{ mb: 1 }}
                        renderValue={selected =>
                          Array.isArray(selected)
                            ? selected
                                .map(
                                  id =>
                                    providerOptions.find(opt => opt.id === id)
                                      ?.name,
                                )
                                .filter(Boolean)
                                .join(', ')
                            : ''
                        }
                      >
                        {providerOptions.map(providerOption => (
                          <MenuItem
                            key={providerOption.id}
                            value={providerOption.id}
                          >
                            <Checkbox
                              checked={isProviderSelected(
                                inputValues[item.key],
                                providerOption.id,
                              )}
                            />
                            {providerOption.name}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : item.label === 'Product' ? (
                      <Select
                        label="Product"
                        variant="outlined"
                        size="small"
                        value={inputValues[item.key] || ''}
                        onChange={event => handleInputChange(event, item.key)}
                      >
                        {productOptions.map(productOption => (
                          <MenuItem
                            key={productOption.id}
                            value={productOption.id}
                          >
                            {productOption.name}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : item.label === 'Time Frame' ? (
                      <DatePicker
                        value={startDate}
                        onChange={date => {
                          setStartDate(date);
                          setInputValues(prev => ({
                            ...prev,
                            [item.key]: date ? format(date, 'yyyy-MM-dd') : '',
                          }));
                        }}
                        label={'Time Frame'}
                        slotProps={{
                          textField: {
                            margin: 'normal',
                            variant: 'outlined',
                            fullWidth: true,
                            name: 'start',
                          },
                        }}
                      />
                    ) : item.key === 'assignee' ? (
                      <Autocomplete
                        options={members}
                        loading={membersLoading}
                        value={selectedAssignee}
                        inputValue={assigneeInput}
                        getOptionLabel={option =>
                          typeof option === 'string'
                            ? option
                            : getOrganizationMemberLabel(option)
                        }
                        isOptionEqualToValue={(a, b) => a.user_id === b.user_id}
                        onChange={(_e, value) => {
                          setSelectedAssignee(
                            value && typeof value !== 'string' ? value : null,
                          );
                        }}
                        onInputChange={(_e, value, reason) => {
                          setAssigneeInput(value);
                          if (reason === 'input') {
                            setSelectedAssignee(null);
                            if (membersSearchTimerRef.current) {
                              window.clearTimeout(membersSearchTimerRef.current);
                            }
                            membersSearchTimerRef.current = window.setTimeout(
                              () => loadMembers(value.trim() || undefined),
                              300,
                            );
                          }
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label="Assignee"
                            variant="outlined"
                            size="small"
                            placeholder="Select member or type name"
                            fullWidth
                            onKeyDown={handleKeyDown}
                            sx={{ mb: 1 }}
                          />
                        )}
                        fullWidth
                        sx={{ mb: 1 }}
                      />
                    ) : (
                      <TextField
                        label={item.label}
                        variant="outlined"
                        size="small"
                        value={inputValues[item.key] || ''}
                        onChange={event => handleInputChange(event, item.key)}
                        fullWidth
                        onKeyDown={handleKeyDown}
                        sx={{ mb: 1 }}
                      />
                    )}
                    <Button
                      sx={{
                        color: theme => theme.palette.action.disabled,
                        '&:not(:disabled)': {
                          color: '#fff',
                        },
                        '&.Mui-disabled': {
                          color: '#000',
                        },
                      }}
                      variant="contained"
                      size="small"
                      onClick={() => handleSave(item.key)}
                      disabled={
                        item.key === 'assignee'
                          ? !selectedAssignee && !assigneeInput.trim()
                          : item.key === 'provider'
                            ? !inputValues['provider'] ||
                              (Array.isArray(inputValues['provider']) &&
                                inputValues['provider'].length === 0)
                            : !inputValues[item.key] ||
                              (typeof inputValues[item.key] === 'string' &&
                                !inputValues[item.key].toString().trim())
                      }
                    >
                      Save
                    </Button>
                  </Box>
                )}
              </Grid>
            ))}
            <Grid item xs={12}>
              <Typography
                variant="h6"
                sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}
              >
                Identification
              </Typography>
            </Grid>
            {identificationFilters.map(item => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={item.key}>
                <MenuItem onClick={() => handleMenuItemClick(item.key)}>
                  <Typography sx={{ fontSize: '0.75rem' }}>
                    {item.label}
                  </Typography>
                </MenuItem>
                {openItem === item.key && (
                  <Box sx={{ p: 2 }}>
                    {/* Removed Gender filter */}
                    {false ? (
                      <Select
                        label={item.label}
                        variant="outlined"
                        size="small"
                        value={inputValues[item.key] || ''}
                        onChange={event => handleInputChange(event, item.key)}
                        fullWidth
                        sx={{ mb: 1 }}
                      >
                        {genderOptions.map(genderOption => (
                          <MenuItem
                            key={genderOption.id}
                            value={genderOption.id}
                          >
                            {genderOption.name}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : item.label === 'Issuing Country' ? (
                      <Select
                        labelId="country-select-label"
                        id="country-select"
                        value={selectedCountry}
                        label="Select a Country"
                        onChange={event => handleChangeList(event, item.key)}
                        fullWidth
                        sx={{ mb: 1 }}
                      >
                        {Object.entries(countryNames).map(([code, name]) => (
                          <MenuItem key={code} value={code}>
                            {name}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : item.label === 'Document Type' ? (
                      <Select
                        label="Document Type"
                        variant="outlined"
                        size="small"
                        value={inputValues[item.key] || ''}
                        onChange={event => handleInputChange(event, item.key)}
                        fullWidth
                        sx={{ mb: 1 }}
                      >
                        {documentTypes.map(docType => (
                          <MenuItem key={docType} value={docType}>
                            {docType}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : item.label === 'Document Front Subtype' ? (
                      <Select
                        label="Document Front Subtype"
                        variant="outlined"
                        size="small"
                        value={inputValues[item.key] || ''}
                        onChange={event => handleInputChange(event, item.key)}
                        fullWidth
                        sx={{ mb: 1 }}
                      >
                        {documentFrontSubtypeOptions.map(option => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : item.label === 'Document Back Subtype' ? (
                      <Select
                        label="Document Back Subtype"
                        variant="outlined"
                        size="small"
                        value={inputValues[item.key] || ''}
                        onChange={event => handleInputChange(event, item.key)}
                        fullWidth
                        sx={{ mb: 1 }}
                      >
                        {documentBackSubtypeOptions.map(option => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : item.label === 'Issue Date' ? (
                      <DatePicker
                        value={dateConfig[item.label].value}
                        onChange={date => {
                          dateConfig[item.label].handler(date);
                          setInputValues(prev => ({
                            ...prev,
                            [item.key]: date ? format(date, 'yyyy-MM-dd') : '',
                          }));
                        }}
                        label={item.label}
                        slotProps={{
                          textField: {
                            margin: 'normal',
                            variant: 'outlined',
                            fullWidth: true,
                            name: 'start',
                          },
                        }}
                      />
                    ) : (
                      <TextField
                        label={item.label}
                        variant="outlined"
                        size="small"
                        value={inputValues[item.key] || ''}
                        onChange={event => handleInputChange(event, item.key)}
                        fullWidth
                        onKeyDown={handleKeyDown}
                        sx={{ mb: 1 }}
                      />
                    )}
                    <Button
                      sx={{
                        color: theme => theme.palette.action.disabled,
                        '&:not(:disabled)': {
                          color: '#fff',
                        },
                        '&.Mui-disabled': {
                          color: '#000',
                        },
                      }}
                      className="text-white"
                      variant="contained"
                      size="small"
                      onClick={() => handleSave(item.key)}
                      disabled={
                        item.key === 'provider'
                          ? !inputValues['provider'] ||
                            (Array.isArray(inputValues['provider']) &&
                              inputValues['provider'].length === 0)
                          : !inputValues[item.key] ||
                            (typeof inputValues[item.key] === 'string' &&
                              !inputValues[item.key].toString().trim())
                      }
                    >
                      Save
                    </Button>
                  </Box>
                )}
              </Grid>
            ))}
            <Grid item xs={12}>
              <Typography
                variant="h6"
                sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}
              >
                Proof of Address
              </Typography>
            </Grid>
            {proofOfAddressFilters.map(item => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={item}>
                <MenuItem onClick={() => handleMenuItemClick(item)}>
                  <Typography sx={{ fontSize: '0.75rem' }}>{item}</Typography>
                </MenuItem>
                {openItem === item && (
                  <Box sx={{ p: 2 }}>
                    {item === 'Document Type' ? (
                      <Select
                        label="Document Type"
                        variant="outlined"
                        size="small"
                        value={inputValues[item] || ''}
                        onChange={event => handleInputChange(event, item)}
                        fullWidth
                        sx={{ mb: 1 }}
                      >
                        {proofOfAddressOptions.map(option => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <TextField
                        label={item}
                        variant="outlined"
                        size="small"
                        value={inputValues[item] || ''}
                        onChange={event => handleInputChange(event, item)}
                        fullWidth
                        onKeyDown={handleKeyDown}
                        sx={{ mb: 1 }}
                      />
                    )}
                    <Button
                      sx={{
                        color: theme => theme.palette.action.disabled,
                        '&:not(:disabled)': {
                          color: '#fff',
                        },
                        '&.Mui-disabled': {
                          color: '#000',
                        },
                      }}
                      variant="contained"
                      size="small"
                      onClick={() => handleSave(item)}
                      disabled={
                        item === 'provider'
                          ? !inputValues['provider'] ||
                            (Array.isArray(inputValues['provider']) &&
                              inputValues['provider'].length === 0)
                          : !inputValues[item] ||
                            (typeof inputValues[item] === 'string' &&
                              !inputValues[item].toString().trim())
                      }
                    >
                      Save
                    </Button>
                  </Box>
                )}
              </Grid>
            ))}
            <Grid item xs={12}>
              <Typography
                variant="h6"
                sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}
              >
                Device Data
              </Typography>
            </Grid>
            {deviceDataFilters.map(item => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={item}>
                <MenuItem onClick={() => handleMenuItemClick(item)}>
                  <Typography sx={{ fontSize: '0.75rem' }}>{item}</Typography>
                </MenuItem>
                {openItem === item && (
                  <Box sx={{ p: 2 }}>
                    <TextField
                      label={item}
                      variant="outlined"
                      size="small"
                      value={inputValues[item] || ''}
                      onChange={event => handleInputChange(event, item)}
                      fullWidth
                      onKeyDown={handleKeyDown}
                      sx={{ mb: 1 }}
                    />
                    <Button
                      sx={{
                        color: theme => theme.palette.action.disabled,
                        '&:not(:disabled)': {
                          color: '#fff',
                        },
                        '&.Mui-disabled': {
                          color: '#000',
                        },
                      }}
                      variant="contained"
                      size="small"
                      onClick={() => handleSave(item)}
                      disabled={
                        item === 'provider'
                          ? !inputValues['provider'] ||
                            (Array.isArray(inputValues['provider']) &&
                              inputValues['provider'].length === 0)
                          : !inputValues[item] ||
                            (typeof inputValues[item] === 'string' &&
                              !inputValues[item].toString().trim())
                      }
                    >
                      Save
                    </Button>
                  </Box>
                )}
              </Grid>
            ))}
          </Grid>
        </Panel>
      </div>
    </Popover>
  );
}
